// Legacy naming: the mascot system predates the Screen Tim rebrand (Aug 2026).
// "Magnum" in component/context/lib names refers to what is now Tim, the mascot.
// Kept to avoid repo-wide churn; user-facing strings say Tim.
import { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react'
import { EngineResponse } from '@/lib/magnumMoment'

export type Mood = 'neutral' | 'happy' | 'encouraging' | 'excited'

export type SaysVariant = 'default' | 'warning' | 'success'

interface Quip {
  text: string
  mood: Mood
  timestamp: number
}

/**
 * A message the current screen wants Tim to deliver — an empty state, a warning,
 * an explanation of why a button is disabled. Unlike a quip it is not a reaction;
 * it describes the state of the page and stays up while that state holds, so it
 * takes precedence over whatever the engine last said.
 */
export interface Announcement {
  id: string
  node: ReactNode
  variant: SaysVariant
}

interface MagnumContextValue {
  quip: Quip | null
  say: (text: string, mood?: Mood) => void
  handleEngineResponse: (response: EngineResponse, correctMood?: Mood) => void
  clear: () => void
  isFrameIdle: boolean
  setFrameIdle: (idle: boolean) => void

  /** True while a MagnumCompanion is mounted and can speak for the page. */
  hasCompanion: boolean
  registerCompanion: () => () => void

  /**
   * A full-screen moment (the launch ceremony) taking Tim over. The shell
   * companion stands down while one is up, so he is never on screen twice.
   */
  isTakenOver: boolean
  claimTakeover: () => () => void

  announcement: Announcement | null
  announce: (a: Announcement) => void
  /** Retracts an announcement; ignored if a later one already replaced it. */
  retract: (id: string) => void

  /**
   * Claims the right to draw Tim's face on screens with no companion. Exactly
   * one holder at a time — everyone else renders a bubble alone.
   */
  claimPortrait: (id: string) => () => void
  portraitOwner: string | null
  /** False for the default context, i.e. rendered outside any MagnumProvider. */
  hasProvider: boolean
}

const noop = () => {}
const noopRelease = () => noop

const MagnumContext = createContext<MagnumContextValue>({
  quip: null,
  say: noop,
  handleEngineResponse: noop,
  clear: noop,
  isFrameIdle: false,
  setFrameIdle: noop,
  hasCompanion: false,
  registerCompanion: noopRelease,
  isTakenOver: false,
  claimTakeover: noopRelease,
  announcement: null,
  announce: noop,
  retract: noop,
  claimPortrait: noopRelease,
  portraitOwner: null,
  hasProvider: false,
})

export function MagnumProvider({ children }: { children: ReactNode }) {
  const [quip, setQuip] = useState<Quip | null>(null)
  const [isFrameIdle, setFrameIdle] = useState(false)
  const [announcement, setAnnouncement] = useState<Announcement | null>(null)
  const [companions, setCompanions] = useState(0)
  const [takeovers, setTakeovers] = useState(0)

  // Ordered claims; the first still-mounted one owns the face.
  const claimsRef = useRef<string[]>([])
  const [portraitOwner, setPortraitOwner] = useState<string | null>(null)

  const say = useCallback((text: string, mood: Mood = 'neutral') => {
    setQuip({ text, mood, timestamp: Date.now() })
  }, [])

  const handleEngineResponse = useCallback((response: EngineResponse, correctMood: Mood = 'happy') => {
    if (response.line) {
      const mood = response.effects?.length ? 'excited' : correctMood
      setQuip({ text: response.line.text, mood, timestamp: Date.now() })
    }
  }, [])

  const clear = useCallback(() => setQuip(null), [])

  const registerCompanion = useCallback(() => {
    setCompanions((n) => n + 1)
    return () => setCompanions((n) => Math.max(0, n - 1))
  }, [])

  const claimTakeover = useCallback(() => {
    setTakeovers((n) => n + 1)
    return () => setTakeovers((n) => Math.max(0, n - 1))
  }, [])

  const announce = useCallback((a: Announcement) => setAnnouncement(a), [])

  const retract = useCallback((id: string) => {
    setAnnouncement((prev) => (prev && prev.id === id ? null : prev))
  }, [])

  const claimPortrait = useCallback((id: string) => {
    claimsRef.current = [...claimsRef.current, id]
    setPortraitOwner(claimsRef.current[0])
    return () => {
      claimsRef.current = claimsRef.current.filter((c) => c !== id)
      setPortraitOwner(claimsRef.current[0] ?? null)
    }
  }, [])

  return (
    <MagnumContext.Provider value={{
      quip, say, handleEngineResponse, clear, isFrameIdle, setFrameIdle,
      hasCompanion: companions > 0, registerCompanion,
      isTakenOver: takeovers > 0, claimTakeover,
      announcement, announce, retract,
      claimPortrait, portraitOwner, hasProvider: true,
    }}>
      {children}
    </MagnumContext.Provider>
  )
}

export function useMagnum() {
  return useContext(MagnumContext)
}
